import React from "react";
import { gsap } from "gsap";
export const AUTOPLAY_DURATION = 10_000;
export function useContentGallery(itemCount: number) {
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const mediaContainerRef = React.useRef<HTMLDivElement>(null);
  const accordionRef = React.useRef<HTMLDivElement>(null);
  const timelineRef = React.useRef<gsap.core.Timeline | null>(null);
  const zoomTweenRef = React.useRef<gsap.core.Tween | null>(null);
  const activeIndexRef = React.useRef(activeIndex);
  activeIndexRef.current = activeIndex;
  const animVersionRef = React.useRef(0);
  const hasInitializedRef = React.useRef(false);
  const animateIn = React.useCallback((container: HTMLDivElement) => {
    const allElements = container.querySelectorAll("[data-animate]");
    gsap.set(allElements, {
      clearProps: "all"
    });
    const title = container.querySelector('[data-animate="title"]');
    const description = container.querySelector('[data-animate="description"]');
    const specs = container.querySelector('[data-animate="specs"]');
    const cta = container.querySelector('[data-animate="cta"]');
    const tl = gsap.timeline({
      defaults: {
        ease: "cubic-bezier(0.125, 0.425, 0.27, 1)"
      }
    });
    if (title) tl.fromTo(title, {
      opacity: 0,
      y: 16
    }, {
      opacity: 1,
      y: 0,
      duration: 0.5
    }, 0);
    if (description) tl.fromTo(description, {
      opacity: 0,
      y: 16
    }, {
      opacity: 1,
      y: 0,
      duration: 0.5
    }, 0.1);
    if (specs) tl.fromTo(specs, {
      opacity: 0,
      y: 16
    }, {
      opacity: 1,
      y: 0,
      duration: 0.5
    }, 0.2);
    if (cta) tl.fromTo(cta, {
      opacity: 0,
      y: 16
    }, {
      opacity: 1,
      y: 0,
      duration: 0.5
    }, 0.3);
    timelineRef.current = tl;
    return tl;
  }, []);
  const animateOut = React.useCallback((container: HTMLDivElement) => {
    const elements = container.querySelectorAll("[data-animate]");
    return gsap.timeline().to(elements, {
      y: -20,
      opacity: 0,
      duration: 0.3,
      stagger: 0.04,
      ease: "power2.in"
    });
  }, []);
  const changeSlide = React.useCallback((newIndex: number) => {
    if (newIndex === activeIndexRef.current) return;
    const version = ++animVersionRef.current;
    const container = contentRef.current;
    if (timelineRef.current) {
      timelineRef.current.kill();
      timelineRef.current = null;
    }
    if (!container) {
      activeIndexRef.current = newIndex;
      setActiveIndex(newIndex);
      return;
    }
    const exitTl = animateOut(container);
    exitTl.eventCallback("onComplete", () => {
      if (animVersionRef.current !== version) return;
      activeIndexRef.current = newIndex;
      setActiveIndex(newIndex);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (animVersionRef.current !== version) return;
          animateIn(container);
        });
      });
    });
  }, [animateIn, animateOut]);
  const handleAutoAdvance = React.useCallback(() => {
    const next = (activeIndexRef.current + 1) % (itemCount || 1);
    changeSlide(next);
  }, [itemCount, changeSlide]);
  const toggleAccordion = React.useCallback(() => {
    const el = accordionRef.current;
    if (!el) return;
    if (isExpanded) {
      setIsExpanded(false);
      gsap.to(el, {
        height: 0,
        opacity: 0,
        duration: 0.4,
        ease: "power2.inOut"
      });
    } else {
      setIsExpanded(true);
      gsap.set(el, {
        height: "auto",
        opacity: 1
      });
      const fullHeight = el.scrollHeight;
      gsap.fromTo(el, {
        height: 0,
        opacity: 0
      }, {
        height: fullHeight,
        opacity: 1,
        duration: 0.4,
        ease: "power2.inOut",
        clearProps: "height"
      });
    }
  }, [isExpanded]);
  React.useEffect(() => {
    const container = contentRef.current;
    if (!container || hasInitializedRef.current) return;
    hasInitializedRef.current = true;
    requestAnimationFrame(() => {
      animateIn(container);
    });
  }, [animateIn]);
  React.useEffect(() => {
    if (zoomTweenRef.current) {
      zoomTweenRef.current.kill();
      zoomTweenRef.current = null;
    }
    const container = mediaContainerRef.current;
    if (!container) return;
    const zoomEl = container.querySelector(`[data-media-idx="${activeIndex}"] [data-zoom]`);
    if (!zoomEl) return;
    zoomTweenRef.current = gsap.fromTo(zoomEl, {
      scale: 1.06
    }, {
      scale: 1,
      duration: AUTOPLAY_DURATION / 1000,
      ease: "power1.out"
    });
  }, [activeIndex]);
  React.useEffect(() => {
    return () => {
      if (timelineRef.current) timelineRef.current.kill();
      if (zoomTweenRef.current) zoomTweenRef.current.kill();
    };
  }, []);
  return {
    activeIndex,
    isExpanded,
    contentRef,
    mediaContainerRef,
    accordionRef,
    changeSlide,
    handleAutoAdvance,
    toggleAccordion
  };
}